import { PropertyValueMap } from 'lit'
import { property } from 'lit/decorators.js'

import { InputStatus } from '@/common/types'

import { DdsElement } from '../dds-hu-element'

const CheckedAttributes = [
  'value',
  'checked',
  'selected',
  'disabled',
  'readonly',
]

type Constructor<T = Record<string, unknown>> = {
  new (...args: any[]): T
  prototype: T
}

export interface FormElementInterface {
  name: string
  defaultValue?: any
  form?: string
  formElement?: HTMLFormElement | null
  disabled: boolean
  required: boolean
  readonly: boolean
  invalid: boolean
  value?: any
  status?: InputStatus
  internals: ElementInternals
  validity: ValidityState
  validationMessage: string
  willValidate: boolean
  innerInput: HTMLElement
  autocomplete: string
  setValidity(): void
  checkValidity(): boolean
  reportValidity(): boolean
  onInput(event?: InputEvent): void
  onKeydown(event?: KeyboardEvent): void
  onBlur(): void
  onFocus(): void
  onChange(event?: InputEvent): void
  handleInvalid(event: Event): void
}

export function FormElementMixin<T extends Constructor<DdsElement>>(
  constructor: T,
): T & Constructor<FormElementInterface> {
  class FormElement extends constructor {
    $input!: HTMLInputElement
    @property({ reflect: true }) name?: string
    @property() defaultValue?: any
    @property({ reflect: true }) form?: string
    @property({ type: Boolean, reflect: true }) disabled = false
    @property({ type: Boolean, reflect: true, attribute: true }) required =
      false
    @property({ type: Boolean, reflect: true }) readonly = false
    @property({ type: Boolean }) silent = true
    @property({ type: Boolean, reflect: true }) invalid = false
    @property({ reflect: true }) status?: InputStatus
    @property() autocomplete = 'off'

    @property({ attribute: true, reflect: true }) value?: any

    @property({ attribute: false }) internals!: ElementInternals
    public formElement: HTMLFormElement | null | undefined = null

    public get innerInput() {
      return this.$input
    }

    static get formAssociated() {
      return true
    }

    constructor(...args: any[]) {
      super(...args)
      this.internals = this.attachInternals()
    }

    connectedCallback() {
      super.connectedCallback()
      this.formElement = this.closest('form')
      if (!this.defaultValue) {
        this.defaultValue = this.value
      }
    }

    attributeChangedCallback(name: string, oldValue: any, newValue: any) {
      super.attributeChangedCallback(name, oldValue, newValue)
      if (CheckedAttributes.includes(name) && newValue !== oldValue) {
        switch (name) {
          case 'value':
            if (this.nodeName !== 'DAP-DS-BUTTON') {
              this.internals.setFormValue(newValue)
            }
            break
          case 'checked':
            this.internals.setFormValue(newValue !== null ? 'on' : null)
        }
      }
    }

    formResetCallback() {
      this.internals.setValidity({})
      this.internals.setFormValue(this.defaultValue)
    }

    onInput() {
      this.emit('dds-input', { value: this.$input.value })
    }

    onBlur() {
      this.emit('dds-blur')
    }

    onFocus() {
      this.emit('dds-focus')
    }

    onChange() {
      this.emit('dds-change', { value: this.$input.value })
    }

    onKeydown(event: KeyboardEvent) {
      this.emit('dds-keydown', { value: this.$input.value, event })
    }

    handleInvalid(event: Event) {
      if (this.silent) {
        event.preventDefault()
      }
    }

    protected async firstUpdated(
      _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
    ) {
      this.$input = this.shadowRoot?.getElementById('input') as HTMLInputElement

      if (this.nodeName !== 'DAP-DS-BUTTON') {
        this.internals.setFormValue(this.value)
        this.setValidity()
      }
    }

    setValidity() {
      if (this.$input) {
        if (!this.$input.checkValidity()) {
          this.internals.setValidity(
            this.$input.validity,
            this.validationMessage,
            this.$input,
          )
        } else {
          this.internals.setValidity({})
        }
      }
    }

    public get validity() {
      return this.$input.validity
    }

    public get validationMessage() {
      return ' '
    }

    public get willValidate() {
      return this.$input.willValidate
    }

    public checkValidity() {
      return this.$input.checkValidity()
    }

    public reportValidity() {
      return this.$input.reportValidity()
    }
  }

  return FormElement as Constructor<FormElementInterface> & T
}
